<template>
  <div class="header-container" :class="{ 'is-mobile': mobile }">
    <template v-if="mobile">
      <div class="mobile-top-row d-flex align-items-center">
        <button
          type="button"
          class="mobile-icon-btn mobile-search-btn"
          aria-label="Search"
          @click="toggleMobileSearch"
        >
          <fa icon="search" fixed-width />
        </button>
        <div class="mobile-brand-wrap">
          <router-link
            :to="{ name: user ? 'home' : 'welcome' }"
            class="d-inline-flex align-items-center text-decoration-none"
          >
            <img
              v-if="activeHeaderLogo && activeHeaderLogo.url"
              :src="activeHeaderLogo.url"
              alt="Site Logo"
              class="header-logo"
            >
            <h2 v-else class="text-black mb-0 mobile-brand">
              <span class="brand-bold">PACIFIC</span>
              <span class="brand-thin">SILK</span>
            </h2>
          </router-link>
        </div>
        <div class="mobile-top-right">
          <div
            v-if="user"
            class="d-flex align-items-center justify-content-end mobile-actions"
          >
            <span v-if="userInitials" class="mobile-initials">{{ userInitials }}</span>
            <popper
              v-if="hasDraftOrderPermission"
              ref="orderSummaryPreviewPopperRef"
              trigger="clickToToggle"
              :options="{
                placement: 'bottom-end',
                modifiers: { offset: { offset: '0px,10px' } },
              }"
              @created="onOrderSummaryPreviewPopperCreated"
            >
              <div class="popper order-summary-popper">
                <div class="popover-container">
                  <order-summary-preview
                    :loading-popper="loadingPopper"
                    @onProceedToCheckoutClicked="
                      $refs.orderSummaryPreviewPopperRef.doClose()
                    "
                  />
                </div>
              </div>
              <div slot="reference" class="d-flex align-items-center cart-trigger">
                <div class="position-relative">
                  <fa
                    icon="shopping-cart"
                    style="color: #dfdfdf;"
                    fixed-width
                  />
                  <pills
                    class="position-absolute top-0 start-100 translate-middle"
                  >
                    {{
                      activeDraftOrderSummary &&
                        activeDraftOrderSummary.total_count
                    }}
                  </pills>
                </div>
              </div>
            </popper>

            <popper
              ref="userDropdownPopperRef"
              trigger="clickToToggle"
              :options="{
                placement: 'bottom-end',
                modifiers: { offset: { offset: '0px,10px' } },
              }"
            >
              <div class="popper user-dropdown-popper">
                <div class="popover-container">
                  <user-dropdown @closeDropdown="$refs.userDropdownPopperRef.doClose()" />
                </div>
              </div>
              <div
                slot="reference"
                class="avatar-trigger"
              >
                <div class="avatar">
                  <img
                    v-if="user.profile_picture_url"
                    :src="`${user.profile_picture_url}`"
                    alt=""
                    class="rounded-circle"
                    width="36"
                    height="36"
                  >
                  <fa
                    v-else
                    icon="user"
                    fixed-width
                  />
                </div>
              </div>
            </popper>
          </div>
        </div>
      </div>
      <div v-if="user && hasDraftOrderPermission" class="row mobile-order-row">
        <div class="col-12">
          <popper
            ref="draftOrdersDropdownPopperRef"
            :options="{
              placement: 'bottom-start',
              modifiers: { offset: { offset: '0px,10px' } },
            }"
            trigger="clickToToggle"
            @created="onDraftOrderPopperCreated"
          >
            <div class="popper draft-order-popper">
              <div class="popover-container">
                <draft-orders-dropdown
                  :loading-get-draft-orders="loadingGetDraftOrders"
                  @closeDropdown="$refs.draftOrdersDropdownPopperRef.doClose()"
                />
              </div>
            </div>
            <div
              v-if="!activeDraftOrder"
              slot="reference"
              class="mobile-order-trigger no-active"
            >
              No Active Orders
            </div>
            <div
              v-else
              slot="reference"
              class="mobile-order-trigger active"
            >
              <img
                v-if="activeDraftOrder.company_logo_url"
                :src="activeDraftOrder.company_logo_url"
                alt=""
                class="mobile-order-logo"
              >
              <div class="mobile-order-body">
                <div class="mobile-order-title">
                  {{ mobileActiveOrderTitle }}
                </div>
                <div class="mobile-order-meta">
                  <span class="ship-label">ship:</span> {{ getUserStartShipDateOrder }}
                  <span v-if="activeDraftOrder.ref_note" class="ref-note">· {{ activeDraftOrder.ref_note }}</span>
                </div>
              </div>
              <span class="mobile-order-count">
                {{ activeDraftOrderSummary && activeDraftOrderSummary.total_count }}
              </span>
            </div>
          </popper>
        </div>
      </div>
      <div v-if="mobileSearchOpen" class="row mobile-search-row">
        <div class="col-12">
          <v-search-input ref="mobileSearchInput" class="w-100" />
        </div>
      </div>
    </template>
    <div v-else class="row align-items-center">
      <div
        class="logo-search align-items-center"
        :class="!desktop ? 'col-4' : 'col-3'"
      >
        <div v-if="!desktop">
          <router-link
            :to="{ name: user ? 'home' : 'welcome' }"
            class="d-flex align-items-center text-decoration-none"
          >
            <img
              v-if="activeHeaderLogo && activeHeaderLogo.url"
              :src="activeHeaderLogo.url"
              alt="Site Logo"
              class="header-logo"
            >
            <h2 v-else class="text-black mb-0">
              PACIFIC SILK
            </h2>
          </router-link>
        </div>
        <GlobalSearch :class="!desktop ? 'search-box' : 'w-100'" />
      </div>
      <div :class="!desktop ? 'col-1' : 'col-2'">
        <div
          v-if="editModeMenus.show"
          class="d-flex align-items-center"
        >
          <toggle-switch
            id="edit-mode"
            class-name="me-2"
            :value="editModeMenus.show"
            @change="onEditModeChanged"
          />
          <span
            class="text-black"
            style="font: normal normal normal 14px/16px Arial;"
          >EDIT mode</span>
        </div>
      </div>
      <div
        v-if="desktop"
        class="col-4"
      >
        <router-link
          :to="{ name: user ? 'home' : 'welcome' }"
          class="d-flex align-items-center text-decoration-none"
        >
          <img
            v-if="activeHeaderLogo && activeHeaderLogo.url"
            :src="activeHeaderLogo.url"
            alt="Site Logo"
            class="header-logo"
          >
          <h2 v-else class="text-black mb-0">
            PACIFIC SILK
          </h2>
        </router-link>
      </div>
      <div :class="!desktop ? 'col-7' : 'col-3'">
        <div
          v-if="user"
          class="d-flex align-items-center justify-content-end header-actions"
        >
          <popper
            v-if="hasDraftOrderPermission"
            ref="draftOrdersDropdownPopperRef"
            :options="{
              placement: 'top-end',
              modifiers: { offset: { offset: '0px,38px' } },
            }"
            trigger="clickToToggle"
            @created="onDraftOrderPopperCreated"
          >
            <div class="popper draft-order-popper">
              <div class="popover-container">
                <draft-orders-dropdown
                  :loading-get-draft-orders="loadingGetDraftOrders"
                  @closeDropdown="$refs.draftOrdersDropdownPopperRef.doClose()"
                />
              </div>
            </div>
            <div
              v-if="!activeDraftOrder"
              slot="reference"
              class="user-details active-order-trigger"
            >
              <span class="active-order-trigger__meta">No Active Orders</span>
            </div>
            <div
              v-else
              slot="reference"
              class="user-details active-order-trigger"
            >
              <span class="active-order-trigger__title">Active draft #{{ activeDraftOrder.id }}</span>
              <span class="active-order-trigger__meta">
                Ship {{ activeShipDateShort }}<template v-if="activeDraftOrder.ref_note"> · Ref: {{ activeDraftOrder.ref_note }}</template>
              </span>
            </div>
          </popper>

          <popper
            v-if="hasDraftOrderPermission"
            ref="orderSummaryPreviewPopperRef"
            trigger="clickToToggle"
            :options="{
              placement: 'top-end',
              modifiers: { offset: { offset: '40px,38px' } },
            }"
            @created="onOrderSummaryPreviewPopperCreated"
          >
            <div  class="popper order-summary-popper">
              <div class="popover-container">
                <order-summary-preview
                  :loading-popper="loadingPopper"
                  @onProceedToCheckoutClicked="
                    $refs.orderSummaryPreviewPopperRef.doClose()
                  "
                />
              </div>
            </div>
            <div slot="reference" class="cart-trigger-desktop">
              <span class="position-relative d-inline-flex">
                <svg
                  width="26"
                  height="24"
                  viewBox="0 0 26 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.6"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <path d="M1 2h3.5l2.6 13h13.4l2.5-9.5H6" />
                  <circle cx="9" cy="20.5" r="1.6" />
                  <circle cx="19" cy="20.5" r="1.6" />
                </svg>
                <pills
                  v-if="activeDraftOrderSummary?.total_count"
                  class="position-absolute top-0 start-100 translate-middle"
                >
                  {{ activeDraftOrderSummary.total_count }}
                </pills>
              </span>
              <span v-if="showWholesalePrice" class="price">{{
                formatCurrency(activeDraftOrderSummary?.total_amount)
              }}</span>
            </div>
          </popper>

          <popper
            ref="userDropdownPopperRef"
            trigger="clickToToggle"
            :options="{
              placement: 'top-end',
              modifiers: { offset: { offset: '0px,38px' } },
            }"
          >
            <div class="popper user-dropdown-popper">
              <div class="popover-container">
                <user-dropdown @closeDropdown="$refs.userDropdownPopperRef.doClose()" />
              </div>
            </div>
            <div
              slot="reference"
              class="header-avatar"
            >
              <img
                v-if="user.profile_picture_url"
                :src="`${user.profile_picture_url}`"
                alt=""
                width="52"
                height="52"
              >
              <span v-else aria-hidden="true">{{ userInitials }}</span>
            </div>
          </popper>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState, mapActions, mapMutations } from 'vuex'
import OrderSummaryPreview from './OrderSummaryPreview.vue'
import UserDropdown from './UserDropdown.vue'
import DraftOrdersDropdown from './DraftOrdersDropdown.vue'
import { formatUSDCurrency } from '~/helpers/format'
import 'vue-popperjs/dist/vue-popper.css'
import dayjs from 'dayjs'
import Pills from '~/components/core/Pills'
import ToggleSwitch from '~/components/core/Inputs/ToggleSwitch'
import GlobalSearch from '~/components/common/GlobalSearch/GlobalSearch'
import { SET_MENUS_EDIT_MODE } from '~/store/mutation-types'
import { PERMISSIONS } from '~/constants'
import wholesalePriceVisibility from '~/mixins/wholesalePriceVisibility'

export default {
  components: {
    ToggleSwitch,
    Pills,
    OrderSummaryPreview,
    UserDropdown,
    DraftOrdersDropdown,
    GlobalSearch
  },
  mixins: [wholesalePriceVisibility],
  data: () => ({
    appName: window.config.appName,
    amount: formatUSDCurrency(137.6),
    isShowNewOrderModal: false,
    showDraftOrdersDropdown: false,
    loadingGetDraftOrders: false,
    loadingPopper: false,
    windowWidth: window.innerWidth,
    mobileSearchOpen: false
  }),

  computed: {
    ...mapState({
      editModeMenus: (state) => state.menus.editMode,
      order: (state) => state.orders,
      stateActionRequiredSelectedOption: (state) => state.orders.actionRequiredSelectedOption,
    }),
    ...mapGetters({
      activeDraftOrder: 'orders/activeDraftOrder',
      user: 'auth/user',
      permissions: 'auth/permissions',
      activeHeaderLogo: 'website-header-logo/activeWebsiteHeaderLogo'
    }),
    getUserStartShipDateOrder () {
      if (!this.activeDraftOrder) {
        return ''
      }
      const dateStr = dayjs(this.activeDraftOrder.start_ship_date).format(
        'MM/DD/YYYY'
      )
      return `${dateStr}`
    },
    activeShipDateShort () {
      return this.activeDraftOrder ? dayjs(this.activeDraftOrder.start_ship_date).format('M/D/YY') : ''
    },
    activeDraftOrderSummary () {
      if (!this.activeDraftOrder) {
        return null
      }
      return {
        total_count: this.activeDraftOrder.order_has_products.length,
        total_amount: this.isRouteValid()
              ? this.activeDraftOrder.available_quantity_total
              : this.activeDraftOrder.total
      };
    },
    desktop () {
      return this.windowWidth >= 1280
    },
    laptop () {
      return this.windowWidth >= 1280 && this.windowWidth <= 1439
    },
    tablet () {
      return this.windowWidth < 1279
    },
    mobile () {
      return this.windowWidth < 744
    },
    mobileActiveOrderTitle () {
      const name = this.activeDraftOrder?.company?.name
      if (!name) return ''
      return name.length > 24 ? name.slice(0, 24) + '…' : name
    },
    userInitials () {
      if (!this.user) return ''
      const f = (this.user.first_name || '').trim().charAt(0)
      const l = (this.user.last_name || '').trim().charAt(0)
      const combined = (f + l).toUpperCase()
      if (combined) return combined
      const fallback = (this.user.name || '').trim().charAt(0).toUpperCase()
      return fallback
    },
    hasDraftOrderPermission() {
        return this.permissions.includes(PERMISSIONS.DRAFT_ORDERS);
    }
  },
  async mounted () {
    window.addEventListener('resize', this.handleResize)
    // Fetch the active website header logo
    try {
      await this.fetchActiveWebsiteHeaderLogo()
    } catch (error) {
      // Silently fail - fallback to default text will be shown
    }
    // Load global display flags (e.g. MSRP visibility)
    try {
      await this.fetchPublicSettings()
    } catch (error) {
      // Silently fail - flags default to off
    }
  },
  destroyed () {
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    ...mapActions({
      fetchOrders: 'orders/fetchOrders',
      fetchDraftOrdersList: 'orders/fetchDraftOrdersList',
      refreshActiveDraftOrder: 'orders/refreshActiveDraftOrder',
      userLogout: 'auth/logout',
      fetchActiveWebsiteHeaderLogo: 'website-header-logo/fetchActiveWebsiteHeaderLogo',
      fetchPublicSettings: 'settings/fetchPublicSettings'
    }),
    ...mapMutations({
      setMenusEditMode: `menus/${SET_MENUS_EDIT_MODE}`
    }),
    formatCurrency (amount) {
      return formatUSDCurrency(amount)
    },
    async logout () {
      // Log out the user.
      await this.userLogout()

      // Redirect to login.
      this.$router.push({ name: 'login' })
    },
    openNewOrderModal () {
      this.$modal.show('new-order-modal')
      this.$refs.draftOrdersDropdownPopperRef.doClose()
    },
    toggleShowDropdown () {
      this.showDraftOrdersDropdown = !this.showDraftOrdersDropdown
    },
    async onDraftOrderPopperCreated () {
      this.loadingGetDraftOrders = true
      // Light, draft-only feed drives the list (constant cost, no /api/order).
      await this.fetchDraftOrdersList()
      this.loadingGetDraftOrders = false
      // Keep the active-order cart badge as fresh as before, without blocking the list
      // or pulling the heavy 50-order graph.
      this.refreshActiveDraftOrder()
    },
    async onOrderSummaryPreviewPopperCreated () {
      this.loadingPopper = false
      await this.fetchOrders()
    },
    onEditModeChanged () {
      this.setMenusEditMode({
        show: !this.editModeMenus.show,
        shopByGalleryEditMode: !this.editModeMenus.show
      })
    },
    handleResize () {
      this.windowWidth = window.innerWidth
      if (this.windowWidth >= 744 && this.mobileSearchOpen) {
        this.mobileSearchOpen = false
      }
    },
    toggleMobileSearch () {
      this.mobileSearchOpen = !this.mobileSearchOpen
      if (this.mobileSearchOpen) {
        this.$nextTick(() => {
          const el = this.$refs.mobileSearchInput?.$el || this.$refs.mobileSearchInput
          const input = el && el.querySelector && el.querySelector('input')
          if (input) input.focus()
        })
      }
    },

    isRouteValid() {
          const validRoutes = [
              'checkout-proceed.order-review',
              'checkout-proceed.delivery-options',
              'checkout-proceed.payment-details'
          ];
          return validRoutes.includes(this.$route.name);
    },
  }
}
</script>

<style scoped lang="scss">
::v-deep {
  .vm--container {
    .vm--modal {
      overflow: unset !important;
    }
  }
}

.header-logo {
  max-height: 40px;
  max-width: 200px;
  object-fit: contain;
}

.header-container {
  padding: 10px 15px;
  background-color: #ffffff;
  border-bottom: 5px solid #000000;

  .dropdown-toggle::after {
    display: none;
  }
  .search-box {
    width: 171px;
  }
  .logo-search {
    display: flex;
    @media screen and (max-width: 1279px) {
      display: inline-block;
      padding-top: 12px;
      padding-bottom: 12px;
    }
  }
  .header-actions {
    gap: 32px;
  }
  .user-details {
    cursor: pointer;
  }
  .active-order-trigger {
    display: flex;
    flex-direction: column;
    align-items: center;
    max-width: 300px;
    padding: 10px 20px;
    border: 1px solid #e2e2e2;
    border-radius: 2px;
    font-family: Arial, sans-serif;
    text-align: center;

    &__title {
      font-size: 13px;
      font-weight: 700;
      line-height: 16px;
      white-space: nowrap;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      color: #1a1a1a;
    }

    &__meta {
      max-width: 100%;
      margin-top: 2px;
      font-size: 13px;
      line-height: 16px;
      color: #555555;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
  .avatar {
    svg{
      width: 18px;
      height: 18px;
    }
  }
  .cart-trigger-desktop {
    display: flex;
    align-items: center;
    gap: 14px;
    color: #1a1a1a;
    cursor: pointer;

    .price {
      font: normal normal normal 17px/20px Arial;
    }
  }
  .header-avatar {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 52px;
    height: 52px;
    margin-right: 16px;
    border-radius: 50%;
    overflow: hidden;
    background: #23244f;
    color: #ffffff;
    font: normal normal bold 16px/1 Arial;
    letter-spacing: 0.05em;
    cursor: pointer;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
}

.order-summary-popper {
  .popover-container {
      max-width: 350px;
      margin-right: 22px;
  }
}
.user-dropdown-popper {
  .popover-container {
    padding: 0;
  }
  // Popper shifts the whole box sideways (off-screen) when the arrow's width plus
  // margins exceeds the avatar it points at (18px on mobile). The negative margins
  // make Popper measure this 28px arrow as 18px while it still renders centred.
  &[x-placement^="bottom"] ::v-deep .popper__arrow {
    margin: 0 -5px;
    border-width: 0 14px 14px 14px;
    top: -14px;
  }
}
.draft-order-popper {
  .popover-container {
    padding: 0;
  }
}

.header-container.is-mobile {
  padding: 10px 14px;
  border-bottom-width: 3px;

  .mobile-top-row {
    gap: 8px;
    min-height: 40px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0;
  }

  .mobile-icon-btn {
    background: transparent;
    border: 0;
    padding: 6px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #000;
    min-width: 34px;
    min-height: 34px;
    cursor: pointer;
    flex-shrink: 0;

    svg {
      font-size: 16px;
    }

    &:focus-visible {
      outline: 2px solid #000;
      outline-offset: 2px;
    }
  }

  .mobile-brand-wrap {
    flex: 1 1 auto;
    display: flex;
    justify-content: center;
    align-items: center;
    min-width: 0;
    overflow: hidden;
  }

  .mobile-brand {
    font-family: 'PlayfairDisplay-Medium', 'Playfair Display', Georgia, serif;
    font-size: 22px;
    line-height: 26px;
    letter-spacing: 0.5px;
    white-space: nowrap;

    .brand-bold {
      font-weight: 700;
      margin-right: 4px;
    }
    .brand-thin {
      font-weight: 400;
    }
  }

  .header-logo {
    max-height: 30px;
    max-width: 140px;
  }

  .mobile-top-right {
    flex-shrink: 0;
  }

  .mobile-actions {
    gap: 14px;

    .mobile-initials {
      font-size: 12px;
      font-weight: 700;
      letter-spacing: 0.5px;
      color: #000;
      line-height: 1;
    }

    .cart-trigger, .avatar-trigger {
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      color: #000;

      svg {
        color: #000;
      }
    }

    .avatar {
      display: flex;
      align-items: center;
      justify-content: center;

      svg {
        width: 18px;
        height: 18px;
      }
    }
  }

  .mobile-order-row {
    display: none;
  }

  .mobile-search-row {
    margin-top: 8px;
    margin-bottom: 0;
  }

  .mobile-order-trigger {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 10px;
    background: #f6f6f6;
    border-radius: 6px;
    cursor: pointer;
    font-size: 12px;
    line-height: 14px;
    color: #000;

    &.no-active {
      justify-content: center;
      color: #898989;
    }

    .mobile-order-logo {
      width: 24px;
      height: 24px;
      border-radius: 50%;
      object-fit: cover;
      flex-shrink: 0;
    }

    .mobile-order-body {
      flex: 1;
      min-width: 0;
    }

    .mobile-order-title {
      font-weight: bold;
      font-size: 13px;
      line-height: 15px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .mobile-order-meta {
      font-size: 11px;
      line-height: 13px;
      color: #555;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;

      .ship-label {
        color: #898989;
      }
    }

    .mobile-order-count {
      flex-shrink: 0;
      min-width: 22px;
      height: 22px;
      padding: 0 6px;
      border-radius: 11px;
      background: #000;
      color: #fff;
      font-size: 11px;
      font-weight: bold;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  .mobile-search-row ::v-deep .search-input {
    width: 100%;
  }
}
</style>
